/** PDF viewer using PDF.js canvas rendering with text selection and highlights.
 *
 * Renders the compiled PDF directly onto a <canvas> element using PDF.js.
 * Renders an overlying PDF.js TextLayer for selectable text, and a HighlightLayer
 * rendering persistent collaborative highlights that scale smoothly with zoom
 * and survive recompilation.
 */

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import "pdfjs-dist/web/pdf_viewer.css";
import type { Annotation, AnnotationInput, Rect } from "../types";

// Point PDF.js to the bundled worker script
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface Props {
  pdfUrl: string | null;
  document: string;
  compileId: string;
  annotations?: Annotation[];
  selectedAnnId?: number | null;
  onCreateAnnotation?: (ann: AnnotationInput) => void;
  onSelectAnnotation?: (id: number) => void;
}

/** Methods exposed by PdfViewer via ref for external navigation. */
export interface PdfViewerHandle {
  /** Navigate to a page and coordinate. */
  goToPosition: (page: number, x: number, y: number) => void;
  /** Navigate directly to a page. */
  goToPage: (page: number) => void;
}

const HIGHLIGHT_COLORS = [
  { name: "Yellow", value: "#ffeb3b" },
  { name: "Green", value: "#a5d6a7" },
  { name: "Blue", value: "#90caf9" },
  { name: "Orange", value: "#ffcc80" },
  { name: "Pink", value: "#f48fb1" },
];

export const PdfViewer = forwardRef<PdfViewerHandle, Props>(
  function PdfViewer(
    {
      pdfUrl,
      document: docPath,
      compileId,
      annotations = [],
      selectedAnnId = null,
      onCreateAnnotation,
      onSelectAnnotation,
    },
    ref,
  ): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerRef = useRef<HTMLDivElement>(null);
    const pageWrapperRef = useRef<HTMLDivElement>(null);
    const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

    const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [scale, setScale] = useState(1.2);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pageDims, setPageDims] = useState<{ width: number; height: number }>({
      width: 0,
      height: 0,
    });

    // Selection toolbar state
    const [selectionMenu, setSelectionMenu] = useState<{
      text: string;
      rects: Rect[];
      x: number;
      y: number;
    } | null>(null);
    const [commentDraft, setCommentDraft] = useState("");
    const [selectedColor, setSelectedColor] = useState("#ffeb3b");
    const [showCommentInput, setShowCommentInput] = useState(false);

    // Fetch PDF bytes and load document via PDF.js.
    useEffect(() => {
      if (!pdfUrl) {
        setPdfDoc(null);
        setNumPages(0);
        setCurrentPage(1);
        return;
      }
      let cancelled = false;
      setLoading(true);
      setError(null);

      fetch(pdfUrl, { credentials: "include" })
        .then(async (resp) => {
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          return resp.arrayBuffer();
        })
        .then((buffer) => {
          if (cancelled) return null;
          const data = new Uint8Array(buffer);
          return pdfjsLib.getDocument({ data }).promise;
        })
        .then((doc) => {
          if (cancelled || !doc) return;
          setPdfDoc(doc);
          setNumPages(doc.numPages);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("PDF load error", err);
          setError(err instanceof Error ? err.message : "Failed to load PDF");
          setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, [pdfUrl, compileId]);

    // Render the active page onto the canvas and textLayer.
    useEffect(() => {
      const doc = pdfDoc;
      const canvas = canvasRef.current;
      const textLayerDiv = textLayerRef.current;
      if (!doc || !canvas || currentPage < 1 || currentPage > numPages) return;

      let cancelled = false;

      // Cancel any ongoing render task before starting a new one.
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      setLoading(true);
      doc.getPage(currentPage)
        .then(async (page) => {
          if (cancelled) return;
          const viewport = page.getViewport({ scale });
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;

          setPageDims({ width: viewport.width, height: viewport.height });

          const renderTask = page.render({ canvasContext: ctx, viewport });
          renderTaskRef.current = renderTask;

          await renderTask.promise;
          if (cancelled) return;

          // Render TextLayer for selectable text
          if (textLayerDiv) {
            textLayerDiv.innerHTML = "";
            textLayerDiv.style.setProperty("--scale-factor", `${scale}`);
            textLayerDiv.style.width = `${viewport.width}px`;
            textLayerDiv.style.height = `${viewport.height}px`;

            try {
              const textContent = await page.getTextContent();
              if (!cancelled) {
                const textLayer = new pdfjsLib.TextLayer({
                  textContentSource: textContent,
                  container: textLayerDiv,
                  viewport,
                });
                await textLayer.render();
              }
            } catch (tErr) {
              console.warn("Text layer render notice:", tErr);
            }
          }

          setLoading(false);
          renderTaskRef.current = null;
        })
        .catch((renderErr: unknown) => {
          const isCancelled =
            renderErr &&
            typeof renderErr === "object" &&
            "name" in renderErr &&
            (renderErr as { name: string }).name === "RenderingCancelledException";
          if (!isCancelled) {
            console.error("PDF render error", renderErr);
          }
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
          renderTaskRef.current = null;
        }
      };
    }, [pdfDoc, currentPage, scale, numPages]);

    // Handle user selecting text with mouse
    const handleMouseUp = useCallback(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        // If clicking away without text, don't dismiss immediately if typing comment
        if (!showCommentInput) setSelectionMenu(null);
        return;
      }

      const text = sel.toString().trim();
      if (!text || text.length === 0) {
        if (!showCommentInput) setSelectionMenu(null);
        return;
      }

      const pageWrapper = pageWrapperRef.current;
      if (!pageWrapper) return;

      const pageBox = pageWrapper.getBoundingClientRect();
      const range = sel.getRangeAt(0);
      const clientRects = Array.from(range.getClientRects());
      if (clientRects.length === 0) return;

      // Calculate normalized rects (relative to page dimensions, 0..1)
      const normalizedRects: Rect[] = clientRects.map((cr) => ({
        x: Math.max(0, Math.min(1, (cr.left - pageBox.left) / pageBox.width)),
        y: Math.max(0, Math.min(1, (cr.top - pageBox.top) / pageBox.height)),
        width: Math.max(0.001, Math.min(1, cr.width / pageBox.width)),
        height: Math.max(0.001, Math.min(1, cr.height / pageBox.height)),
      }));

      // Calculate toolbar coordinates relative to pageWrapper
      const firstRect = clientRects[0];
      const menuX = firstRect.left - pageBox.left + firstRect.width / 2;
      const menuY = firstRect.top - pageBox.top - 10;

      setSelectionMenu({
        text,
        rects: normalizedRects,
        x: menuX,
        y: menuY,
      });
      setShowCommentInput(false);
      setCommentDraft("");
    }, [showCommentInput]);

    const handleCreateHighlight = (withComment = false) => {
      if (!selectionMenu) return;
      const commentText = withComment ? commentDraft.trim() : "";

      onCreateAnnotation?.({
        document: docPath,
        page: currentPage,
        type: "highlight",
        selected_text: selectionMenu.text,
        rects: selectionMenu.rects,
        comment: commentText,
        color: selectedColor,
      });

      // Clear selection
      window.getSelection()?.removeAllRanges();
      setSelectionMenu(null);
      setShowCommentInput(false);
      setCommentDraft("");
    };

    // Forward ref navigation (SyncTeX and jump-to-page)
    useImperativeHandle(
      ref,
      () => ({
        goToPosition: (page: number, _x: number, _y: number): void => {
          if (page >= 1 && (!numPages || page <= numPages)) {
            setCurrentPage(page);
          }
        },
        goToPage: (page: number): void => {
          if (page >= 1 && (!numPages || page <= numPages)) {
            setCurrentPage(page);
          }
        },
      }),
      [numPages],
    );

    const goPrev = useCallback((): void => {
      setCurrentPage((p) => Math.max(1, p - 1));
      setSelectionMenu(null);
    }, []);

    const goNext = useCallback((): void => {
      setCurrentPage((p) => Math.min(numPages, p + 1));
      setSelectionMenu(null);
    }, [numPages]);

    const zoomIn = useCallback((): void => {
      setScale((s) => Math.min(3.0, Number((s + 0.2).toFixed(1))));
    }, []);

    const zoomOut = useCallback((): void => {
      setScale((s) => Math.max(0.4, Number((s - 0.2).toFixed(1))));
    }, []);

    const fitWidth = useCallback((): void => {
      const container = containerRef.current;
      const doc = pdfDoc;
      if (!container || !doc) return;
      doc.getPage(currentPage).then((page) => {
        const viewport0 = page.getViewport({ scale: 1 });
        const availableWidth = container.clientWidth - 48;
        if (availableWidth > 50) {
          setScale(Number((availableWidth / viewport0.width).toFixed(2)));
        }
      });
    }, [pdfDoc, currentPage]);

    /** Download the compiled PDF as a file. */
    const handleDownload = async (): Promise<void> => {
      if (!pdfUrl) return;
      try {
        const resp = await fetch(pdfUrl, { credentials: "include" });
        if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const stem = docPath.replace(/\.tex$/, "").split("/").pop() || "document";
        a.download = `${stem}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("PDF download error", err);
      }
    };

    // Filter annotations on the active page
    const pageAnnotations = annotations.filter(
      (a) => !a.is_deleted && a.page === currentPage,
    );

    if (!pdfUrl) {
      return (
        <div className="pdf-pane">
          <div className="empty" style={{ flex: 1, display: "flex", alignItems: "center" }}>
            Compile a document to see the PDF preview.
          </div>
        </div>
      );
    }

    return (
      <div className="pdf-pane" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          className="pdf-toolbar"
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px" }}
        >
          <button onClick={goPrev} disabled={currentPage <= 1} title="Previous page">
            ‹
          </button>
          <span className="page" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <input
              type="number"
              min={1}
              max={numPages || 1}
              value={currentPage}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= numPages) {
                  setCurrentPage(val);
                }
              }}
              style={{ width: 44, textAlign: "center", fontSize: 12, padding: "2px 4px" }}
            />
            <span>/ {numPages || "—"}</span>
          </span>
          <button onClick={goNext} disabled={currentPage >= numPages} title="Next page">
            ›
          </button>

          <span style={{ margin: "0 4px", opacity: 0.4 }}>|</span>

          <button onClick={zoomOut} title="Zoom out">
            −
          </button>
          <span style={{ fontSize: 11, minWidth: 40, textAlign: "center" }}>
            {Math.round(scale * 100)}%
          </span>
          <button onClick={zoomIn} title="Zoom in">
            +
          </button>
          <button onClick={fitWidth} title="Fit to width" style={{ fontSize: 11, padding: "2px 6px" }}>
            Fit Width
          </button>

          <span style={{ margin: "0 4px", opacity: 0.4 }}>|</span>

          <span style={{ fontSize: 11, opacity: 0.8 }}>
            Highlights: {pageAnnotations.length}
          </span>

          <span style={{ flex: 1 }} />
          {loading && (
            <span className="spinner-text" style={{ fontSize: 11 }}>
              <span className="spinner" /> Loading...
            </span>
          )}
          <button onClick={handleDownload} title="Download PDF file">
            ⬇ Download
          </button>
        </div>

        <div
          className="pdf-container"
          ref={containerRef}
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: 16,
            background: "var(--bg-secondary)",
            position: "relative",
          }}
        >
          {error ? (
            <div className="empty" style={{ color: "var(--error)" }}>
              {error}
            </div>
          ) : (
            <div
              ref={pageWrapperRef}
              className="pdf-page-wrapper"
              onMouseUp={handleMouseUp}
              style={{
                position: "relative",
                width: pageDims.width || "auto",
                height: pageDims.height || "auto",
                boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
                background: "#ffffff",
                borderRadius: 2,
              }}
            >
              {/* Canvas rendered by PDF.js */}
              <canvas
                ref={canvasRef}
                style={{
                  display: "block",
                  background: "#ffffff",
                }}
              />

              {/* PDF.js standard transparent TextLayer for native selection */}
              <div
                ref={textLayerRef}
                className="textLayer"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  pointerEvents: "auto",
                  opacity: 1,
                }}
              />

              {/* HighlightLayer rendering persistent colored boxes */}
              <div
                className="highlightLayer"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                {pageAnnotations.map((ann) =>
                  ann.rects.map((r, i) => (
                    <div
                      key={`${ann.id}-${i}`}
                      className={`pdf-highlight-rect ${selectedAnnId === ann.id ? "selected" : ""}`}
                      style={{
                        position: "absolute",
                        left: `${r.x * 100}%`,
                        top: `${r.y * 100}%`,
                        width: `${r.width * 100}%`,
                        height: `${r.height * 100}%`,
                        backgroundColor: ann.color || "#ffeb3b",
                        opacity: selectedAnnId === ann.id ? 0.65 : 0.38,
                        borderBottom: selectedAnnId === ann.id ? "2px solid #ff9800" : "none",
                        borderRadius: 2,
                        cursor: "pointer",
                        pointerEvents: "auto",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectAnnotation?.(ann.id);
                      }}
                      title={`@${ann.username}: ${ann.comment ? ann.comment : ann.selected_text}`}
                    />
                  )),
                )}
              </div>

              {/* Floating selection action toolbar */}
              {selectionMenu && (
                <div
                  className="selection-toolbar"
                  style={{
                    position: "absolute",
                    left: Math.max(10, Math.min((pageDims.width || 600) - 270, selectionMenu.x - 135)),
                    top: Math.max(8, selectionMenu.y - (showCommentInput ? 110 : 44)),
                    zIndex: 100,
                    background: "var(--bg-primary, #ffffff)",
                    color: "var(--text-primary, #222222)",
                    border: "1px solid var(--border, #ccc)",
                    borderRadius: 6,
                    padding: "6px 10px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    width: 260,
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
                    {/* Color swatches */}
                    <div style={{ display: "flex", gap: 4 }}>
                      {HIGHLIGHT_COLORS.map((c) => (
                        <span
                          key={c.value}
                          onClick={() => setSelectedColor(c.value)}
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            background: c.value,
                            cursor: "pointer",
                            border: selectedColor === c.value ? "2px solid #333" : "1px solid rgba(0,0,0,0.2)",
                            boxSizing: "border-box",
                          }}
                          title={c.name}
                        />
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        className="primary"
                        style={{ fontSize: 11, padding: "2px 6px" }}
                        onClick={() => handleCreateHighlight(false)}
                        title="Highlight selected text"
                      >
                        🖍 Highlight
                      </button>
                      <button
                        style={{ fontSize: 11, padding: "2px 6px" }}
                        onClick={() => setShowCommentInput(!showCommentInput)}
                        title="Add a comment with this highlight"
                      >
                        💬 Comment
                      </button>
                      <button
                        style={{ fontSize: 11, padding: "2px 4px", background: "transparent", border: "none", cursor: "pointer" }}
                        onClick={() => setSelectionMenu(null)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {showCommentInput && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                      <input
                        type="text"
                        placeholder="Write a comment..."
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleCreateHighlight(true);
                        }}
                        autoFocus
                        style={{
                          fontSize: 11,
                          padding: "4px 6px",
                          borderRadius: 3,
                          border: "1px solid var(--border, #ccc)",
                        }}
                      />
                      <button
                        className="primary"
                        style={{ fontSize: 11, padding: "2px 8px", alignSelf: "flex-end" }}
                        onClick={() => handleCreateHighlight(true)}
                      >
                        Save Comment
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);
